import React from "react";
import cx from "classnames";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ExpandMore as ExpandMoreIcon } from '@vedmarket/ui-kit';
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";

import { COLORS } from "../../../../../../../config/constants";
import {getMergeVatRates} from "../modal_chooseVat";
import DeclarationsList from "./components/declarationsList";

import './modal_table.scss';

type Props = {
  children: React.ReactNode
  title: string
}

const ModalTableAccordion = ({children, title}: Props) => (
  <Accordion sx={{
    boxShadow: 'none',
    backgroundColor: COLORS.GRAY_50,
    border: '1px solid ' + COLORS.SLIGHTLY_GRAY,
    marginBottom: 3,
    ':before': {
      opacity: 0
    },
    '&.Mui-expanded': {
      marginBottom: 3,
      paddingBottom: 0,
      ':last-of-type': {
        marginBottom: 0,
      },
      ':before': {
        opacity: 0
      },
    }
  }}>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        padding: '8px 24px',
        '&.Mui-expanded': {
          borderBottom: 'none',
          '.MuiAccordionSummary-content': {
            margin: '16px 0',
          },
          '.MuiAccordionSummary-expandIconWrapper svg': {
            width: '24px',
            height: '24px',
          }
        },
        '.MuiAccordionSummary-content': {
          margin: '16px 0',
        },
        '.MuiAccordionSummary-expandIconWrapper svg': {
          width: '24px',
          height: '24px',
        }
      }}
    >
      <Typography variant='body1' sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ padding: '0' }}>
      {children}
    </AccordionDetails>
  </Accordion>
)

type PropsModalTable = {
  tnvedInfoVat: Vat[]
  tnvedInfoHierarchy: CodeTnved[]
  importInfo?: CommonInfoImportListImport
  isHelp?: boolean
  code?: string
}

const ModalTable = ({tnvedInfoVat, tnvedInfoHierarchy, importInfo, isHelp, code}: PropsModalTable) => {

  const mergeVatRates = getMergeVatRates(tnvedInfoVat);
  const nameVat = Object.keys(mergeVatRates)[0];
  const vatData = Object.values(mergeVatRates)[0];

  const { t } = useTranslation()

  return <>
    <div className='code-table-wrapper'>
      <ModalTableAccordion title={t('calc__text__customs__HSPosition')}>
        <table className='code-table'>
          <tbody>
            {tnvedInfoHierarchy.map((item, index) => (<tr key={item.Level}>
              <td>
                <Typography variant='body2' sx={{ color: COLORS.MEDIUM_GRAY, fontWeight: 500 }}>
                  {item.Code}
                </Typography>
              </td>
              <td>
                <Typography variant='body2' sx={{ color: COLORS.BLACK, paddingBottom: '4px', lineHeight: 1.6 }}>
                  {item.Name.charAt(0).toUpperCase() + item.Name.slice(1).toLowerCase()}
                </Typography>
              </td>
            </tr>))}
          </tbody>
        </table>
      </ModalTableAccordion>
      <ModalTableAccordion title={t('calc__text__customs__customsDutiesAndImport')}>
        <table className='code-table'>
          <tbody>
            {importInfo?.Value && (
              <tr>
                <td>
                  <Typography variant='body2' sx={{ color: COLORS.MEDIUM_GRAY, paddingBottom: 2, maxWidth: '154px' }}>
                    {t('calc__text__customs__basicRateOfCustomsDuty')}
                  </Typography>
                </td>
                <td>
                  <Typography variant='body2' sx={{ color: COLORS.BLACK, fontWeight: 700 }}>
                    {importInfo.Value}
                  </Typography>
                  {importInfo?.Order &&
                    <Typography variant='body2' sx={{ color: COLORS.BLACK }}>
                      {importInfo.Order}
                    </Typography>}
                </td>
              </tr>
            ) }
            <tr>
              <td>
                <Typography variant='body2' sx={{ color: COLORS.MEDIUM_GRAY }}>
                  {t('common__text__vatRate')}
                </Typography>
              </td>
              <td>
                {vatData[0].Directory.RuName !== null && (<Typography variant='body2' sx={{ color: COLORS.BLACK, fontWeight: 500}}>
                  {vatData[0].Directory.RuName}
                </Typography>)}
                <Typography variant='body2' sx={{ color: COLORS.BLACK }}>
                  {nameVat !== 'null' ? <>{nameVat}<br/></> : null}
                  <a href={vatData[0].Link} target='_blank' className='code-table__link'>{vatData[0].Document}</a>
                </Typography>
                <Typography variant='body2' sx={{ color: COLORS.BLACK }}>
                  <ul className={cx('code-table__list', {
                    'code-table__list_noNameVat': nameVat === 'null',
                  })}>
                    {vatData.map((vat, index) => (
                      <li key={index}>
                        <span className='code-table__bold'>{vat?.ValueDetail?.ValueCount}{vat?.ValueDetail?.ValueUnit}</span> {vat.Condition}
                      </li>
                    ))}
                  </ul>
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </ModalTableAccordion>
      {isHelp && <ModalTableAccordion title={t('calc__text__packaging__examplesOfDeclarations')}>
        <DeclarationsList code={code}/>
      </ModalTableAccordion>}
    </div>
  </>
}

export default ModalTable
